// ============================================================================
// marketWalletHistory — расшифровка истории личного счёта мастера.
//
// Биллинг отдаёт операции с client_ref/meta (ключи идемпотентности сервера,
// см. internal/markettake):
//   • "take:<id заявки>"   — списание за взятие;
//   • "refund:<id заявки>" — возврат за взятие (meta.request_id);
//   • deposit от платёжного слоя — пополнение (meta.provider).
// Здесь операция превращается в понятную строку: «Списано за заявку №…»,
// «Возврат за заявку №…» и т.д. Номера заявок — из документа mkt_requests
// (штатный серверный отбор master_id = текущий мастер).
// ============================================================================

import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { logSwallowed } from '@/lib/errors';
import {
  MARKET_REQUEST_TABLE,
  cellsOf,
} from '@/lib/market';

const REQUESTS_PAGE_SIZE = 200;
const MAX_LOOKUPS = 20;

export type MarketWalletOperationTone = 'in' | 'out' | 'reserve' | 'neutral';

export interface MarketWalletOperation {
  id: string;
  kind: string;
  state: string;
  amount: number;
  createdAt: string;
  title: string;
  subtitle: string;
  requestId: string;
  requestNumber: string;
  tone: MarketWalletOperationTone;
  /** Операция ещё не завершена (ожидает подтверждения). */
  pending: boolean;
  /** Операция не выполнена/снята — сумма не влияет на баланс. */
  inactive: boolean;
}

export const WALLET_STATE_LABELS: Record<string, string> = {
  pending: 'Ожидает подтверждения',
  done: 'Выполнено',
  failed: 'Не выполнено',
  released: 'Снято',
};

const OPERATION_KIND_LABELS: Record<string, string> = {
  deposit: 'Пополнение счёта',
  debit: 'Списание',
  hold: 'Резерв средств',
  capture: 'Списание из резерва',
  release: 'Резерв снят',
  transfer: 'Перевод',
  refund: 'Возврат',
};

interface RequestHint {
  number: string;
  category: string;
  descr: string;
}

function parseMeta(raw: string): Record<string, unknown> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch (e) {
    logSwallowed(e, 'Кошелёк мастера: разбор meta операции');
    return {};
  }
}

function textOf(meta: Record<string, unknown>, key: string): string {
  const value = meta[key];
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/** Идентификатор заявки из meta или client_ref операции. */
export function operationRequestId(raw: unknown): string {
  const row = raw as Record<string, unknown> | null;
  const meta = parseMeta(String(row?.meta || ''));
  const fromMeta = textOf(meta, 'request_id');
  if (fromMeta) return fromMeta;
  const clientRef = String(row?.clientRef || '');
  if (clientRef.startsWith('take:')) return clientRef.slice('take:'.length);
  if (clientRef.startsWith('refund:')) return clientRef.slice('refund:'.length);
  return '';
}

function shortRequestId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

/** Одна страница заявок (без полного перебора — кошелёк не тянет всю базу). */
async function fetchRequestRows(
  filters: Array<{ column: string; value: string }>,
  pageSize: number,
): Promise<TableDataRow[]> {
  const res: any = await getSDK().metadata.listTableData({
    schema: 'public',
    table: MARKET_REQUEST_TABLE,
    pageSize,
    page: 0,
    includeMarked: false,
    orderBy: 'created_at',
    orderDesc: true,
    filters,
  } as any);
  return (res.rows || []) as TableDataRow[];
}

function hintFromRow(row: TableDataRow): RequestHint {
  const cells = cellsOf(row);
  return {
    number: cells.get('number') || '',
    category: cells.get('category_id_presentation') || '',
    descr: (cells.get('descr') || '').replace(/\s+/g, ' ').trim(),
  };
}

/**
 * Номера заявок для строк истории: сначала одна страница «моих» заявок,
 * затем точечные чтения для тех, что не попали в страницу (не больше 20).
 * Любая ошибка — только в журнал, история остаётся доступной.
 */
export async function loadRequestHints(
  userId: string,
  ids: string[],
): Promise<Map<string, RequestHint>> {
  const hints = new Map<string, RequestHint>();
  if (!userId || ids.length === 0) return hints;
  try {
    const rows = await fetchRequestRows(
      [{ column: 'master_id', value: userId }],
      REQUESTS_PAGE_SIZE,
    );
    for (const row of rows) hints.set(row.id, hintFromRow(row));
  } catch (e) {
    logSwallowed(e, 'Кошелёк мастера: номера заявок в истории');
  }
  const missing = ids.filter((id) => !hints.has(id)).slice(0, MAX_LOOKUPS);
  if (missing.length === 0) return hints;
  const settled = await Promise.allSettled(
    missing.map((id) => fetchRequestRows([{ column: 'id', value: id }], 1)),
  );
  settled.forEach((result, index) => {
    if (result.status !== 'fulfilled') {
      logSwallowed(result.reason, 'Кошелёк мастера: номер заявки операции');
      return;
    }
    const row = result.value[0];
    if (row) hints.set(missing[index], hintFromRow(row));
  });
  return hints;
}

function toneForKind(kind: string): MarketWalletOperationTone {
  if (kind === 'deposit' || kind === 'refund') return 'in';
  if (kind === 'debit' || kind === 'capture') return 'out';
  if (kind === 'hold') return 'reserve';
  return 'neutral';
}

/** Преобразовать операцию биллинга в понятную строку истории. */
export function buildWalletOperation(
  raw: any,
  hints: Map<string, RequestHint>,
  providerTitles: Map<string, string>,
): MarketWalletOperation {
  const kind = String(raw?.kind || '');
  const state = String(raw?.state || '');
  const amount = Number(raw?.amount || 0);
  const clientRef = String(raw?.clientRef || '');
  const meta = parseMeta(String(raw?.meta || ''));
  const requestId = operationRequestId(raw);
  const hint = requestId ? hints.get(requestId) : undefined;
  const requestNumber = hint?.number || '';
  const requestLabel = requestNumber
    ? `заявку № ${requestNumber}`
    : requestId
      ? `заявку ${shortRequestId(requestId)}`
      : '';
  const isTake = kind === 'debit' && (clientRef.startsWith('take:') || !!textOf(meta, 'request_id'));
  const isRefund = kind === 'refund' || clientRef.startsWith('refund:');

  let title = OPERATION_KIND_LABELS[kind] || 'Операция';
  let subtitle = '';
  let tone = toneForKind(kind);

  if (isTake) {
    title = requestLabel ? `Списано за ${requestLabel}` : 'Списано за заявку';
    subtitle = [hint?.category, hint?.descr].filter(Boolean).join(' · ').slice(0, 140)
      || 'Оплата взятия заявки';
    tone = 'out';
  } else if (isRefund) {
    title = requestLabel ? `Возврат за ${requestLabel}` : 'Возврат за заявку';
    subtitle = [textOf(meta, 'reason'), textOf(meta, 'comment')]
      .filter(Boolean).join(' · ').slice(0, 140)
      || 'Возврат стоимости взятия';
    tone = 'in';
  } else {
    switch (kind) {
      case 'deposit': {
        const providerCode = textOf(meta, 'provider');
        const providerTitle = providerCode ? providerTitles.get(providerCode) : '';
        title = 'Пополнение счёта';
        subtitle = providerTitle ? `Поступление · ${providerTitle}` : 'Зачисление на личный счёт';
        break;
      }
      case 'hold':
        subtitle = 'Сумма зарезервирована, но ещё не списана';
        break;
      case 'release':
        subtitle = 'Зарезервированная сумма освобождена';
        break;
      case 'capture':
        subtitle = 'Резерв превращён в списание';
        break;
      case 'debit':
        subtitle = 'Списание с личного счёта';
        break;
      case 'transfer':
        subtitle = 'Перевод между счетами';
        break;
      default:
        subtitle = 'Операция по личному счёту';
        break;
    }
  }
  if (!subtitle) subtitle = 'Операция по личному счёту';

  const pending = state === 'pending';
  const inactive = state === 'failed' || (kind === 'hold' && state === 'released');
  if (inactive) subtitle = `Не влияет на баланс · ${subtitle}`;

  return {
    id: String(raw?.id || ''),
    kind,
    state,
    amount: Number.isFinite(amount) ? amount : 0,
    createdAt: String(raw?.createdAt || ''),
    title,
    subtitle,
    requestId,
    requestNumber,
    tone,
    pending,
    inactive,
  };
}

/** Свежие операции — первыми (сервер уже сортирует, но порядок не гарантируем). */
export function sortWalletOperations(items: MarketWalletOperation[]): MarketWalletOperation[] {
  return [...items].sort((a, b) => {
    const at = a.createdAt || '';
    const bt = b.createdAt || '';
    if (at === bt) return 0;
    return at < bt ? 1 : -1;
  });
}
